import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Plane } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const Airline = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [airlines,         setAirlines]         = useState([]);
  const [filteredAirlines, setFilteredAirlines] = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [showModal,        setShowModal]        = useState(false);
  const [editMode,         setEditMode]         = useState(false);
  const [submitLoading,    setSubmitLoading]    = useState(false);
  const [activeOnly,       setActiveOnly]       = useState(false);
  const [errors,           setErrors]           = useState({});

  const [formData, setFormData] = useState({ AirlineID: null, Airline: '', IsActive: true });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchAirlines();
  }, [companyId, yearId, activeOnly]);

  useEffect(() => { filterAirlines(); }, [searchTerm, airlines]);

  const fetchAirlines = async () => {
    setLoading(true);
    try {
      const params = activeOnly ? { table: 'airline', active: '1' } : { table: 'airline' };
      const res = await apiClient.get('/LookupMaster.php', { params });
      if (res.data.success) setAirlines(res.data.data || []);
      else alert('Failed to fetch airlines');
    } catch { alert('Error fetching airlines'); }
    finally { setLoading(false); }
  };

  const filterAirlines = () => {
    if (!searchTerm.trim()) setFilteredAirlines(airlines);
    else setFilteredAirlines(airlines.filter(a =>
      a.Airline.toLowerCase().includes(searchTerm.toLowerCase())
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.Airline.trim()) errs.Airline = 'Airline name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/LookupMaster.php?table=airline', {
        ...formData, IsActive: formData.IsActive ? 1 : 0,
      });
      if (res.data.success) {
        alert(editMode ? 'Airline updated successfully!' : 'Airline created successfully!');
        closeModal(); fetchAirlines();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      alert('Error saving airline: ' + (err.response?.data?.message || err.message));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = async (airline) => {
    try {
      const res = await apiClient.get(`/LookupMaster.php?table=airline&id=${airline.AirlineID}`);
      if (res.data.success) {
        setFormData({ AirlineID: res.data.data.AirlineID, Airline: res.data.data.Airline, IsActive: res.data.data.IsActive === 1 });
        setEditMode(true); setShowModal(true); setErrors({});
      }
    } catch { alert('Error loading airline details'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this airline?')) return;
    try {
      const res = await apiClient.delete('/LookupMaster.php?table=airline', { data: { AirlineID: id } });
      if (res.data.success) { alert('Airline deleted successfully!'); fetchAirlines(); }
      else alert('Error: ' + res.data.message);
    } catch { alert('Error deleting airline'); }
  };

  const resetForm  = () => { setFormData({ AirlineID: null, Airline: '', IsActive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const inputCls = (err) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#4f46e5] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <Plane size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div
        className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Plane size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Airline Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage airline carriers</p>
          </div>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0
            text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}
        >
          <Plus size={17} />
          Add Airline
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        {/* Search */}
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#4f46e5] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]
          transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search airlines..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium
              placeholder:text-gray-400 placeholder:font-normal"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Active only toggle */}
        <label className="flex items-center gap-2.5 cursor-pointer px-4 py-2.5 bg-gray-50
          border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150 select-none">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={e => setActiveOnly(e.target.checked)}
            className="w-4 h-4 cursor-pointer accent-[#0d1c6b]"
          />
          <span className="text-sm font-semibold text-gray-700">Active Only</span>
        </label>

        {/* Count */}
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredAirlines.length}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading airlines...
          </div>
        ) : filteredAirlines.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Plane size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No airlines found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['ID', 'Airline', 'Status', 'Actions'].map(h => (
                    <th key={h}
                      className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAirlines.map(airline => (
                  <tr key={airline.AirlineID}
                    className="border-b border-gray-100 transition-all duration-150
                      hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5 font-mono text-[13px] font-bold text-violet-700 bg-violet-50 w-16">
                      {airline.AirlineID}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <Plane size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{airline.Airline}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        airline.IsActive === 1
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                          : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {airline.IsActive === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(airline)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                            bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150
                            hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5
                            hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(airline.AirlineID)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                            bg-red-100 border-red-400 text-red-600 transition-all duration-150
                            hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5
                            hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-[480px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
            >
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0"
                style={{ color: '#fff' }}>
                <Plane size={18} />
                {editMode ? 'Edit Airline' : 'Add New Airline'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0
                  bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-7">

              {/* Section label */}
              <div className="flex items-center gap-2 text-xs font-bold text-gray-600 uppercase tracking-wide
                px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-5"
                style={{ borderLeftColor: NAVY }}>
                <Plane size={14} />
                Airline Information
              </div>

              {/* Airline name */}
              <div className="mb-5">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Airline Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.Airline}
                  onChange={e => setFormData({ ...formData, Airline: e.target.value })}
                  maxLength="255"
                  placeholder="e.g., Emirates, Qatar Airways"
                  className={inputCls(errors.Airline)}
                  autoFocus
                />
                {errors.Airline && (
                  <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.Airline}</p>
                )}
              </div>

              {/* Active checkbox */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50
                  border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input
                    type="checkbox"
                    checked={formData.IsActive}
                    onChange={e => setFormData({ ...formData, IsActive: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]"
                  />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg
                    cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300
                    disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer
                    transition-all duration-200 hover:-translate-y-0.5
                    hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)]
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Airline;